import React from 'react';
import { SelectLabel, OptionalSection, Icon, ToastContext } from '@weezevent/nacre';
import SeatIcon from './SeatIcon';
import css from './modal-summary.module.css';
import OptionalItem from './OptionalItem';
import { useTranslation } from 'react-i18next';
import useAppContext from '../../../../../../../context/useAppContext';
import LoadingCard from '../../../../../../../components/loadingCard';
import { Booking } from '../../../../../../../models/bookingModel';

export default function SummaryAttendee({
    attendee,
    selectedZone,
    form,
    index,
    categories,
    previousZone,
    availability,
    onChangeForm = () => {},
    currentInventory,
    sync,
    unbookBooking,
    onChangeAllForm = () => {}
}) {
    const { t } = useTranslation();
    const [{ organization, event }] = useAppContext();
    const [loading, setLoading] = React.useState(false);
    const toast = React.useContext(ToastContext);

    const booking = React.useMemo(() => {
        return attendee.booking_uuid;
    }, [attendee]);

    const firstName = React.useMemo(() => {
        if (Object.keys(attendee.form).length) {
            return attendee.form['ws-2'];
        }
        return null;
    }, [attendee, onChangeForm]);

    const lastName = React.useMemo(() => {
        if (Object.keys(attendee.form).length) {
            return attendee.form['ws-3'];
        }
        return null;
    }, [attendee, onChangeForm]);

    const onChangeFormAttendee = React.useCallback(
        (index, key, value) => {
            let attendees = [...form.attendees];
            let attendee = attendees.find(a => a.id === index);
            attendee.form[key] = value;
            onChangeForm('attendees', attendees);
        },
        [onChangeForm]
    );

    const rateOptions = React.useMemo(() => {
        let rates = [];
        if (!availability?.children?.length) {
            const zone = previousZone?.children?.find(zone => zone.name === selectedZone.name);
            zone?.categories?.map(catego => {
                catego?.contingents.forEach(ctgt => {
                    if (catego.uuid === attendee.category) {
                        ctgt.rates.forEach(rate => {
                            rates.push({ text: <SeatIcon name={rate.name} color={catego.color} />, value: parseInt(rate.id) });
                        });
                    }
                });
            });
        }
        return rates;
    }, [previousZone, attendee]);

    const categoryRates = React.useMemo(() => {
        const rates = [];
        categories.forEach(category => {
            category.uuid === attendee.category &&
                category.rates.forEach(rate => {
                    rates.push(rate.rate_obj);
                });
        });
        return rates;
    }, [categories, attendee]);

    const selected = React.useMemo(() => {
        let properRate = form.selected_rates.find(s_rate => s_rate.rate.id === attendee.rate_id);
        return properRate.rate.id;
    }, [form, attendee]);

    const handleChange = React.useCallback(
        selected => {
            let booking = new Booking();
            let bookings_uuid_to_unbook = [attendee.booking_uuid.booking_uuid];
            if (bookings_uuid_to_unbook.length) {
                booking
                    .unbook({ bookings: bookings_uuid_to_unbook, chartId: event.chart, organizationId: organization.pk })
                    .then(() => {
                        // Delete attendee and selected rates on success
                        // 1 - Book a new seat and update form with new rate

                        let seats = [{ uuid: attendee.booking_uuid.seat_uuid, external_id: selected }];
                        let attendeeZone = attendee.booking_uuid.seat_zone ? attendee.booking_uuid.seat_zone : 'root';

                        booking.seats = seats;
                        booking.category = attendee.booking_uuid.seat_category;
                        return booking
                            .save({
                                chartId: event.chart,
                                inventoryId: attendee.booking_uuid.inventory,
                                zoneId: attendeeZone,
                                query: {
                                    manual: true
                                }
                            })
                            .then(({ response }) => {
                                let attendees = [...form.attendees];
                                let rates = [...form.selected_rates];

                                // Change attendee
                                let attendeeToChange = attendees.find(att => att.id === attendee.id);
                                attendeeToChange.booking_uuid = response.seats[0];
                                attendeeToChange.rate_id = selected;

                                // Change rate
                                let oldRate = rates.findIndex(sRate => sRate.rate.id === attendee.rate_id && sRate.inventory.uuid === attendee.booking_uuid.inventory);
                                let newRate = rates.find(sRate => sRate.rate.id === selected && sRate.inventory.uuid === attendee.booking_uuid.inventory);

                                if (rates[oldRate].quantity === 1) {
                                    rates.splice(oldRate, 1);
                                } else {
                                    rates[oldRate].quantity -= 1;
                                }

                                if (newRate) {
                                    newRate.quantity += 1;
                                } else {
                                    let rateObj = categoryRates.find(r => r.id === selected);
                                    let data = {
                                        rate: rateObj, // find rate in all available rates
                                        sessionId: null,
                                        inventory: oldRate.inventory,
                                        quantity: 1,
                                        price: rateObj.resolved_price
                                    };
                                    rates.push(data);
                                }
                                onChangeAllForm({ ...form, selected_rates: rates, attendees: attendees });
                                toast.success(t('ticket.discountForm.editSuccess'));
                            })
                            .catch(e => {
                                toast.error(t(`partners.invitation.errors.${e.response.detail ? e.response.detail : 'unknownError'}`));
                            });
                    })
                    .catch(e => {
                        toast.error(t(`partners.invitation.errors.${e.response.detail ? e.response.detail : 'unknownError'}`));
                    });
            }
        },
        [attendee, onChangeAllForm, categoryRates, form, event, organization]
    );

    const handleRemove = React.useCallback(
        att => {
            setLoading(true);

            Promise.all([att])
                .then(promises => {
                    unbookBooking(promises.filter(promise => promise).flat());
                })
                .finally(() => {
                    sync({
                        organizationId: organization?.id,
                        eventId: event?.id,
                        inventoryId: currentInventory,
                        zoneId: selectedZone.uuid,
                        cache: false
                    });
                });
        },
        [unbookBooking, loading, sync, organization, event, currentInventory, selectedZone]
    );

    const renderSelector = React.useMemo(() => {
        if (rateOptions.length <= 1) {
            return (
                <span className={css['summary-attendee-zone']}>
                    {t('ticket.common.rate')}
                    <div>
                        <SeatIcon name={attendee.booking_uuid.rate_name} color={attendee.booking_uuid.rate_category_color} id={attendee.booking_uuid.booking_uuid} />
                    </div>
                </span>
            );
        }
        return (
            <SelectLabel
                className={css['summary-attendee-zone']}
                label={t('ticket.common.rate')}
                options={rateOptions}
                onChange={selected => {
                    handleChange(selected);
                }}
                value={selected}
            />
        );
    }, [rateOptions, selected, attendee]);

    const seatInfos = React.useMemo(() => {
        if (booking.seat_zone_name === undefined) {
            return booking.area_zone_name;
        }
        return `${booking.seat_zone_name} - ${t('ticket.attendees.import.errors.details.seat_row')} ${booking.seat_row} - ${t(
            'ticket.attendees.import.errors.details.seat_seat'
        )} ${booking.seat_seat} - ${t('ticket.attendees.import.errors.details.seat_access')} ${booking.seat_access}`;
    }, [booking]);

    if (loading) {
        return (
            <div className={css['summary-attendee-item']} style={{ height: '200px' }}>
                <LoadingCard loading />
            </div>
        );
    }

    return (
        <div className={css['summary-attendee-item']}>
            <div className={css['summary-attendee-header']}>
                <span>{firstName && lastName ? `${firstName} ${lastName}` : `Participant ${index + 1}`}</span>
                <Icon name="cross" onClick={() => handleRemove(attendee)} size="regular" />
            </div>

            <div className={css['summary-attendee-body']}>
                <span className={css['summary-attendee-zone']}>Zone</span>
                <span>{seatInfos}</span>

                {renderSelector}
                <OptionalSection open={false} onToggle={null} label={t('attendees.addAttendees.withSeats.modalNominative')}>
                    <div className={css['summary-attendee-optional-inputs']}>
                        <OptionalItem name={t('ticket.form.list.default.last_name')} value={lastName} onChange={e => onChangeFormAttendee(attendee.id, 'ws-3', e.target.value)} />
                        <OptionalItem name={t('ticket.form.list.default.first_name')} value={firstName} onChange={e => onChangeFormAttendee(attendee.id, 'ws-2', e.target.value)} />
                    </div>
                </OptionalSection>
            </div>
        </div>
    );
}
